import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronUp, ScrollText } from 'lucide-react'
import type { ZambeBetQuotesResponse } from '@brochure/shared'
import { selectionCount, slipQuoteState, slipSummary, type DraftSlip, type SlipLimitsView } from '@/lib/slip'
import { formatOdds } from '@/lib/zambebet'

type SlipBarProps = { slip: DraftSlip; quotes: ZambeBetQuotesResponse | undefined; limits: SlipLimitsView; onOpen: () => void }

/** Barra fissa in basso su tutte le pagine ZambeBet: eventi, quota totale (o colonne del sistema), avviso se qualcosa è cambiato. */
export function SlipBar({ slip, quotes, limits, onOpen }: SlipBarProps) {
  const { t, i18n } = useTranslation()
  if (selectionCount(slip) === 0) return null
  const summary = slipSummary(slip, limits)
  const state = slipQuoteState(slip, quotes)
  const needsAttention = state.changed.length > 0 || state.closed.length > 0
  return (
    <button
      type="button"
      data-testid="slip-bar"
      onClick={onOpen}
      className="pointer-events-auto mx-auto mt-2 flex min-h-14 w-full max-w-2xl items-center gap-3 rounded-2xl border border-ink/10 bg-sun px-4 text-left font-body text-ink shadow-[0_8px_24px_rgb(0_0_0/0.35)] transition-transform duration-150 active:scale-[0.99]"
    >
      <ScrollText className="size-5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold">
        {t('zambebet.slip.bar', { count: slip.legs.length })}
        {' · '}
        {slip.type === 'system' ? t('zambebet.slip.columns', { count: summary.columns }) : t('zambebet.slip.barOdds', { odds: formatOdds(summary.totalOdds, i18n.language) })}
      </span>
      {needsAttention && <span data-testid="slip-bar-alert" className="size-2.5 shrink-0 rounded-full bg-coral motion-safe:animate-pulse" aria-label={t('zambebet.slip.attention')} role="img" />}
      <ChevronUp className="size-5 shrink-0" aria-hidden="true" />
    </button>
  )
}

/**
 * Fascia fissa in basso delle pagine ZambeBet: le giocate live in attesa (`LiveSlips`) impilate sopra la `SlipBar`.
 * Lascia passare i tocchi tra un biglietto e l'altro; ogni elemento ha il suo `mt-2` e `pointer-events-auto`.
 * Un distanziatore nel flusso, alto quanto la fascia (ResizeObserver), fa sì che il fondo della pagina non resti
 * mai sotto la fascia, qualunque sia il numero di biglietti.
 */
export function SlipDock({ children }: { children: ReactNode }) {
  const dockRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  useLayoutEffect(() => {
    const dock = dockRef.current
    if (!dock) return
    const observer = new ResizeObserver(() => setHeight(dock.offsetHeight))
    observer.observe(dock)
    return () => observer.disconnect()
  }, [])
  return (
    <>
      <div aria-hidden="true" data-testid="slip-dock-spacer" style={{ height }} />
      <div ref={dockRef} className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {children}
      </div>
    </>
  )
}
